import { productData as defaultProductData } from '@/assets/liquid/productData';
import {
    LiquidEngine,
    transformProductSetToProduct
} from '@/utils/liquidEngine';
import {
    AGENT_TYPE,
    AgentConversation,
    agentService,
    EVENT_TYPES,
    MessageType,
    ROLES,
    STATUSES
} from '@common/api/agentService';
import { ProductSet } from '@common/api/searchService';
import { ContextItem } from '@common/components/AgentInput';
import React, {
    createContext, useCallback, useContext,
    useMemo,
    useRef, useState
} from 'react';
import { useAuth } from '../AuthContext';

export interface ChartData {
    date: string;
    timestamp: string;
    values: Array<{
        query: string;
        value: string;
        extracted_value: number;
    }>;
}

export interface LinkData {
    title: string;
    url: string;
    description: string;
}

export interface ProductDetailData {
    product_id: string;
    title: string;
    url: string;
    image_url: string;
    price: string;
    currency: string;
    rating: string;
    reviews: string;
    sales_volume: string;
    platform: string;
}

export interface ChatHistory {
    conversation: AgentConversation;
}

export interface Template {
    productData: ProductSet;
    productSourceUUID: string;
    taskUUID: string;
    liquidCode: string;
    templateUrl: string; // template address
    blobUrl: string;
    createdAt?: string;
}

export interface TaskView {
    taskUUID: string;
    taskType: string;
    taskTitle: string;
}

export interface ChatState {
    /** 消息相关 */
    // 所有会话的消息
    messages: Record<string, MessageType[]>;
    setMessages: React.Dispatch<React.SetStateAction<Record<string, MessageType[]>>>;
    addMessage: (msg: MessageType, conversationId?: string) => void;
    showDetailIndex: number;
    setShowDetailIndex: (index: number) => void;
    results: Record<string, any>;
    addResult: (result: any) => void;
    currentResultId: string | null;
    setCurrentResultId: (id: string | null) => void;

    // 选中的商品
    selectedProducts: ProductDetailData[];
    setSelectedProducts: React.Dispatch<React.SetStateAction<ProductDetailData[]>>;

    // 当前会话 ID
    currentConversationId: string | null;
    setCurrentConversationId: React.Dispatch<React.SetStateAction<string>>;
    // 获取当前会话的消息
    getCurrentMessages: () => MessageType[];
    // 拉取会话消息
    getConversationMessages: (spaceId: string) => void;
    // 是否正在加载会话
    isLoadingConversation: boolean;
    setIsLoadingConversation: (isLoading: boolean) => void;
    // agent 正在执行
    isGenerating: boolean;
    setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;

    // 会话历史相关
    history: AgentConversation[];
    setHistory: React.Dispatch<React.SetStateAction<AgentConversation[]>>;
    isLoadingHistory: boolean;
    setIsLoadingHistory: React.Dispatch<React.SetStateAction<boolean>>;

    // AbortController 相关
    abortControllerRef: React.MutableRefObject<AbortController | null>;
    cancelCurrentRequest: () => void;
    createNewAbortController: () => AbortController;

    // 商详页画布 ref
    productData: ProductSet;
    productSourceUUID: string;
    templates: Template[];
    setTemplates: React.Dispatch<React.SetStateAction<Template[]>>;
    currentTemplate: number;
    setCurrentTemplate: (currentTemplate: number) => void;
    renderPage: (templateCode: string, productData: ProductSet) => Promise<string>;
    updateProductData: (productData: ProductSet) => Promise<void>;
    selectedSelector: string;
    setSelectedSelector: React.Dispatch<React.SetStateAction<string>>;

    // 全局 Chat 显隐控制
    isChatVisible: boolean;
    setIsChatVisible: React.Dispatch<React.SetStateAction<boolean>>;
    // 全局 Context
    context: ContextItem[];
    setContext: React.Dispatch<React.SetStateAction<ContextItem[]>>;
}

const mockTasks: Record<string, MessageType[]> = {}


const initialState: ChatState = {
    messages: {},
    setMessages: () => { },
    addMessage: () => null,
    showDetailIndex: -1,
    setShowDetailIndex: () => { },
    results: {},
    currentResultId: null,
    setCurrentResultId: () => { },
    addResult: () => null,
    selectedProducts: [],
    setSelectedProducts: () => { },
    currentConversationId: null,
    setCurrentConversationId: () => { },
    getCurrentMessages: () => [],
    getConversationMessages: () => null,
    isLoadingConversation: false,
    setIsLoadingConversation: () => { },
    // agent 正在执行
    isGenerating: false,
    setIsGenerating: () => { },

    // 会话历史相关
    history: [],
    setHistory: () => { },
    isLoadingHistory: false,
    setIsLoadingHistory: () => { },

    // AbortController 相关
    abortControllerRef: { current: null },
    cancelCurrentRequest: () => { },
    createNewAbortController: () => new AbortController(),

    // 商详页画布 ref
    productData: {} as ProductSet,
    productSourceUUID: '',
    templates: [],
    setTemplates: () => { },
    currentTemplate: 0,
    setCurrentTemplate: () => { },
    renderPage: () => Promise.resolve(''),
    updateProductData: () => Promise.resolve(),
    selectedSelector: '',
    setSelectedSelector: () => { },
    isChatVisible: false,
    setIsChatVisible: () => { },
    context: [],
    setContext: () => { },
};

export { initialState as chatInitialState };

const ChatContext = createContext<ChatState>(initialState);

export const useChatContext = () => useContext(ChatContext);

export const createChatProvider = () => {
    const { user, profile } = useAuth();
    const [messages, setMessages] = useState<Record<string, MessageType[]>>({});
    const [currentConversationId, setCurrentConversationId] = useState<string>("temp");
    const [showDetailIndex, setShowDetailIndex] = useState<number>(-1);
    const [selectedProducts, setSelectedProducts] = useState<ProductDetailData[]>([]);
    const [results, setResults] = useState<Record<string, any>>({});
    const [currentResultId, setCurrentResultId] = useState<string | null>(null);
    const [isLoadingConversation, setIsLoadingConversation] = useState(false);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [currentTemplate, setCurrentTemplate] = useState(0);
    const liquidEngine = useRef(new LiquidEngine());
    const [selectedSelector, setSelectedSelector] = useState<string>('');
    const [isGenerating, setIsGenerating] = useState(false);
    // 会话历史相关状态
    const [history, setHistory] = useState<AgentConversation[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // 全局 Chat 显隐控制
    const [isChatVisible, setIsChatVisible] = useState(false);
    const [context, setContext] = useState<ContextItem[]>([]);

    // AbortController 相关
    const abortControllerRef = useRef<AbortController | null>(null);

    const createNewAbortController = useCallback(() => {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        return controller;
    }, []);

    const renderPage = useCallback(async (templateCode: string, productData: ProductSet) => {
        const templateData = transformProductSetToProduct(productData);
        return liquidEngine.current.render(templateCode, templateData);
    }, [liquidEngine]);

    // 获取当前会话的消息
    const getCurrentMessages = useCallback(() => {
        if (!currentConversationId) return [];
        return messages[currentConversationId] || [];
    }, [messages, currentConversationId]);

    const addResult = useCallback((result: any) => {
        setResults(prevResults => ({ ...prevResults, [result.id]: result }));
    }, []);


    //     // 获取会话消息
    const getConversationMessages = async (spaceId: string) => {
        // 拉取会话消息
        setIsLoadingConversation(true);
        const conversations = await agentService.getConversations();
        if(!conversations[0]?.id){
            return;
        }
        setCurrentConversationId(conversations[0]?.id || 'temp');
        if(messages[conversations[0]?.id]){
            setIsLoadingConversation(false);
            return;
        }
        try{
            const messages = await agentService.getMessages(conversations[0]?.id);
            if(messages.length){
                const newMessages = messages.map(msg => {
                    if(msg.event_type === EVENT_TYPES.SYSTEM_MESSAGE_COMPLETED){
                        return {
                            ...msg.json_content,
                            event_type: EVENT_TYPES.SYSTEM_MESSAGE_DELTA,
                        }
                    }
                    if(msg.event_type === EVENT_TYPES.TOOL_SUMMARY_COMPLETED){
                        return {
                            ...msg.json_content,
                            event_type: EVENT_TYPES.TOOL_SUMMARY_DELTA,
                        }
                    }
                    return{
                        ...msg.json_content,
                        event_type: msg.event_type,
                    }
                }) as MessageType[]
                
                setMessages({[conversations[0].id]: newMessages});
            }
            setIsLoadingConversation(false);
        }catch(err){
            setIsLoadingConversation(false);
        }
    }

    const addMessage = useCallback((msg: MessageType, conversationId?: string) => {
        const targetConversationId = conversationId || msg.conversation_id || 'temp';
        setMessages(msgs => {
            const conversationMessages = msgs[targetConversationId] || [];
            
            // 对于流式消息，尝试合并相同事件类型的连续消息
            const shouldMerge = (
                msg.event_type === EVENT_TYPES.SYSTEM_MESSAGE_DELTA
                || msg.event_type === EVENT_TYPES.TOOL_SUMMARY_DELTA
            )
            && conversationMessages.length > 0
            && conversationMessages[conversationMessages.length - 1].event_type === msg.event_type
            && conversationMessages[conversationMessages.length - 1].role === msg.role

            if (shouldMerge) {
                // 使用类型断言访问 delta 字段
                if (msg.event_type === EVENT_TYPES.SYSTEM_MESSAGE_DELTA
                     || msg.event_type === EVENT_TYPES.TOOL_SUMMARY_DELTA) {
                    const updatedMessages = [
                        ...conversationMessages.slice(0, -1),
                        msg
                    ];
                    return {
                        ...msgs,
                        [targetConversationId]: updatedMessages
                    };
                }
            }

            if(msg.event_type === EVENT_TYPES.TOOL_COMPLETED){
                (conversationMessages[conversationMessages.length - 1] as any).tool_run_id = msg.tool_run_id;
            }
            if(msg.event_type === EVENT_TYPES.ARTIFACT_COMPLETED){
                (conversationMessages[conversationMessages.length - 1] as any).artifact_id = msg.artifact_id;
            }
            
            // 直接添加新消息
            const updatedMessages = [...conversationMessages, { ...msg}];
            return {
                ...msgs,
                [targetConversationId]: updatedMessages
            };
        });
    }, []);

    const cancelCurrentRequest = useCallback(() => {
        if (abortControllerRef.current && isGenerating) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            addMessage({
                role: ROLES.SYSTEM,
                event_type: EVENT_TYPES.SYSTEM_MESSAGE_DELTA,
                delta: 'Request cancelled',
                content: 'Request cancelled',
                conversation_id: currentConversationId,
                created_at: new Date().toISOString(),
                status: STATUSES.STREAMING,
                agent_type:AGENT_TYPE.AGENT_MODE
            }, currentConversationId);
        }
    }, [addMessage, currentConversationId, isGenerating]);
    
    const productData = useMemo(() => {
        return templates[currentTemplate]?.productData || defaultProductData;
    }, [templates, currentTemplate])

    const productSourceUUID = useMemo(() => {
        return templates[currentTemplate]?.productSourceUUID || '';
    }, [templates, currentTemplate])
    // 更新产品数据
    const updateProductData = useCallback(async (productData: ProductSet) => {
        const newTemplates = await Promise.all(templates.map(async (template) => {
            const blobUrl = await renderPage(template.liquidCode, productData);
            return {
                ...template, productData, blobUrl 
            };
        }));
        setTemplates(newTemplates);
    }, [templates, renderPage]);

    const updateTemplate = useCallback((newTemplate: Template) => {
        // 更新templates
        setTemplates(prevTemplates => {
            // 检查是否已存在相同taskUUID的模板，如果存在则替换，否则添加
            const existingIndex = prevTemplates.findIndex(t => t.taskUUID === newTemplate.taskUUID);
            if (existingIndex >= 0) {
                const newTemplates = [...prevTemplates];
                newTemplates[existingIndex] = newTemplate;
                setCurrentTemplate(prevTemplates.length - 1);
                return newTemplates;
            } else {
                // 按照 created_at 时间顺序插入新模板，最新的在最后
                const newTemplates = [...prevTemplates];
                const insertIndex = newTemplates.findIndex(t => 
                    new Date(t.createdAt!) > new Date(newTemplate.createdAt!));
                            
                if (insertIndex === -1) {
                    // 如果没有找到更晚的时间，插入到最后
                    newTemplates.push(newTemplate);
                } else {
                    // 插入到正确的位置
                    newTemplates.splice(insertIndex, 0, newTemplate);
                }
                setCurrentTemplate(newTemplates.length - 1);
                return newTemplates;
            }
        });
    }, []);
    // 处理生成页面任务开始
    const handleProductTemplateGeneration = useCallback(async (taskUUID: string, task: MessageType) => {
        updateTemplate({
            taskUUID: taskUUID,
            liquidCode: '',
            templateUrl: '',
            blobUrl: '',
            createdAt: task.created_at!,
            productData: productData,
            productSourceUUID: productSourceUUID
        });
    }, [updateTemplate, productData, productSourceUUID]);
    
    const chat = {
        messages,
        setMessages,
        addMessage,
        addResult,
        currentResultId,
        setCurrentResultId,
        results,
        showDetailIndex,
        setShowDetailIndex,
        selectedProducts,
        setSelectedProducts,
        currentConversationId,
        setCurrentConversationId,
        getCurrentMessages,
        getConversationMessages,
        isLoadingConversation,
        setIsLoadingConversation,
        // agent 正在执行
        isGenerating,
        setIsGenerating,
        // 会话历史相关
        history,
        setHistory,
        isLoadingHistory,
        setIsLoadingHistory,
        // AbortController 相关
        abortControllerRef,
        cancelCurrentRequest,
        createNewAbortController,
        // 商详页画布 ref
        productData,
        productSourceUUID,
        templates,
        setTemplates,
        currentTemplate,
        setCurrentTemplate,
        renderPage,
        updateProductData,
        selectedSelector,
        setSelectedSelector,
        isChatVisible,
        setIsChatVisible,
        context,
        setContext
    };

    return chat;
};